import React from 'react';
import { cn } from '@/lib/utils';

// Central status -> tone mapping so every page renders the same status the
// same way. Extend TONE_MAP if a new status string shows up in the API
// data -- never invent a new visual language per-page.
const TONE_MAP = {
  active: 'success',
  enabled: 'success',
  paid: 'success',
  current: 'success',
  healthy: 'success',
  operational: 'success',
  completed: 'success',
  trial: 'sky',
  provisioning: 'sky',
  pending: 'warning',
  degraded: 'warning',
  past_due: 'warning',
  overdue: 'warning',
  legacy: 'warning',
  read_only: 'warning',
  suspended: 'destructive',
  terminated: 'destructive',
  failed: 'destructive',
  error: 'destructive',
  down: 'destructive',
  archived: 'muted',
  internal: 'violet',
  qa: 'violet',
  // Deployment environments (environmentPresentation.js decides the label).
  live: 'success',
  development: 'violet',
  staging: 'sky',
  demo: 'warning',
  unclassified: 'muted',
  // Sample Connection statuses (sampleConnectionPresentation.js).
  sample_connected: 'success',
  sample_pending: 'warning',
  sample_degraded: 'warning',
  sample_not_configured: 'muted',
  sample_expired: 'destructive',
  sample_reauth_required: 'destructive',
  sample_failed: 'destructive',
  sample_unknown: 'muted',
  // Demo Connections item outcomes (demoConnectionsPresentation.js). The
  // vocabulary is `items[].outcome` in demoConnectionsContract.json and this
  // map may not gain a key that is not in it.
  //
  // FOUR OF THE SEVEN ARE MUTED ON PURPOSE. `unknown` means the runtime could
  // not be read, `no_outcome_reported` means it answered and said nothing about
  // this item, and `unrecognised` means it said something this build cannot
  // read. None of the three is a measurement of failure, and drawing any of
  // them destructive states an outcome nobody observed.
  demo_connected: 'success',
  demo_pending: 'warning',
  demo_not_configured: 'muted',
  demo_attention: 'destructive',
  demo_unrecognised: 'muted',
  demo_no_outcome_reported: 'muted',
  demo_unknown: 'muted',
  // Demo Connections concept states (demoConnectionsContract.json:
  // desired.state, attempted.state, actual.state). Prefixed because the three
  // vocabularies share words — `not_requested` and `not_recorded` appear in
  // more than one — and an unprefixed key would silently give one concept's
  // state another concept's tone.
  //
  // DESIRED IS NOT A HEALTH AXIS and is deliberately not green: "the approval
  // is readable" is a statement about a document, and a success tone would be
  // read across the room as "the connections are fine".
  desired_recorded: 'sky',
  desired_not_requested: 'muted',
  desired_unavailable_at_freeze: 'warning',
  desired_not_recorded: 'muted',
  desired_unverified: 'destructive',
  attempted_not_recorded: 'muted',
  attempted_in_progress: 'sky',
  attempted_completed: 'success',
  attempted_failed: 'destructive',
  attempted_unrecognised: 'muted',
  actual_reported: 'sky',
  // WARNING, NOT DESTRUCTIVE. Nothing failed; nothing was measured.
  actual_unavailable: 'warning',
  actual_not_requested: 'muted',
  // ── Onboarding & Access (onboardingPresentation.js) ────────────────────
  //
  // Three axes, three prefixes. They share words with each other and with the
  // families above — `pending` is an invitation state AND a generic one,
  // `failed` is a sample-data state AND a job one — and an unprefixed key
  // would hand one concept another concept's tone silently.
  //
  // WAITING IS NOT A WARNING. `invite_pending` is `sky`, not amber: an
  // invitation sent and not yet accepted is the normal state of every tenant
  // between creation and the customer's first sign-in, and drawing days of
  // ordinary onboarding in the caution tone teaches an operator to stop
  // reading it. Amber is kept for `expired`, where the link has stopped
  // working and there is something to do.
  invite_never_sent: 'muted',
  invite_pending: 'sky',
  invite_expired: 'warning',
  invite_accepted: 'success',
  invite_delivery_failed: 'destructive',
  readiness_ready: 'success',
  readiness_provisioning: 'sky',
  readiness_incomplete: 'warning',
  // `muted`, deliberately, and NOT `warning`. A legacy tenant is assessed and
  // incomplete, but nothing about it is actionable: Repair refuses without an
  // approved configuration to reconcile against. Warning tone would promise a
  // remedy that does not exist.
  readiness_legacy: 'muted',
  readiness_attention: 'destructive',
  // MUTED, and it is the one that matters. `unknown` is a COMPLETED job with
  // no verification report — the absence of a measurement. Green claims one
  // was taken and passed; red claims one was taken and failed.
  readiness_unknown: 'muted',
  seed_not_requested: 'muted',
  seed_applied: 'success',
  seed_partial: 'warning',
  seed_failed: 'destructive',
  seed_partially_removed: 'warning',
  seed_removed: 'muted',
  // ── Platform user accounts (users/userPresentation.js) ─────────────────
  //
  // Prefixed for the reason stated twice above: `active`, `pending` and
  // `suspended` already belong to tenants and subscriptions, and an unprefixed
  // key hands one concept another concept's tone without anyone editing it.
  //
  // `user_unknown` is the one that matters. The page these keys replace fell
  // back to its `active` entry for every status it did not recognise, so a
  // suspended account and a healthy one were the same green tick. Muted here
  // means "this build did not recognise the value", not "fine".
  user_active: 'success',
  // SKY, NOT AMBER — same argument as `invite_pending`. An invitation waiting
  // to be accepted is the ordinary state of a new account, not a caution.
  user_invited: 'sky',
  user_disabled: 'muted',
  user_suspended: 'destructive',
  user_unknown: 'muted',
  inactive: 'muted',
  cancelled: 'muted',
  unknown: 'muted',
};

const TONE_CLASSES = {
  success: 'bg-success/10 text-success ring-success/20',
  warning: 'bg-warning/10 text-warning-text ring-warning/20',
  destructive: 'bg-destructive/10 text-destructive-text ring-destructive-text/20',
  // `--info`, not `--chart-5`: this tone carries "In progress", "Trial",
  // "Provisioning", "Staging" and "Reported", and a chart series colour
  // used as ink measured 2.57:1 on this badge in light.
  sky: 'bg-info/10 text-info-text ring-info/20',
  violet: 'bg-tone-violet/10 text-tone-violet-text ring-tone-violet/20',
  muted: 'bg-muted text-muted-foreground ring-border',
};

const StatusBadge = ({ status, label, dot = true, className }) => {
  const key = (status || '').toLowerCase();
  const tone = TONE_MAP[key] || 'muted';
  const toneClass = TONE_CLASSES[tone];
  const text = label || (status ? status.replace(/_/g, ' ') : 'Unknown');

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium capitalize ring-1 ring-inset',
        toneClass,
        className
      )}
    >
      {dot && (
        <span
          className={cn(
            'h-1.5 w-1.5 rounded-full',
            tone === 'success' && 'bg-success',
            tone === 'warning' && 'bg-warning',
            tone === 'destructive' && 'bg-destructive',
            tone === 'sky' && 'bg-info',
            tone === 'violet' && 'bg-tone-violet',
            tone === 'muted' && 'bg-muted-foreground'
          )}
        />
      )}
      {text}
    </span>
  );
};

export default StatusBadge;
